<script lang="ts">
	import { untrack } from 'svelte'
	import { t } from 'svelte-i18n'
	import type { Lever } from '@semanticedge/persistence-service-noheaders-client/src'
	import {
		Button,
		ButtonSize,
		ButtonType,
		DetailedSelect,
		LeverValueField
	} from '@repo/commons/components'
	import { useSetting, SETTING_KEYS } from '@repo/commons/settings'
	import type {
		DetailedSelectItem,
		FormValidationItem,
		ProjectViewModel,
		TableData
	} from '@repo/commons/models'
	import {
		enumToTranslatedString,
		getLeverValueValidators,
		isLeverResource,
		toLeverDefaultValue,
		toLeverValidationInput,
		validateEmptyString,
		validateForm,
		applyBooleanDefault,
		toLeverFormValue,
		type LeverFormValue
	} from '@repo/commons/utils'
	import { ALL_LEVER_FIELDS_TOUCHED } from './constants.js'
	import type { LeverFormField, LeverFormValues } from './types.js'
	import { toLeverFormValues } from './utils.js'

	type Props = {
		onclose: VoidFunction
		project: ProjectViewModel | null
		editingLever?: TableData | null
		existingLeverIds: readonly string[]
		onSubmit: (lever: Partial<Lever>, isEdit: boolean) => Promise<boolean>
	}

	const { project, onclose, onSubmit, editingLever, existingLeverIds }: Props = $props()

	const { showNodeIds } = useSetting({ showNodeIds: SETTING_KEYS.SHOW_IDS_ON_NODES })

	let formValues = $state<LeverFormValues>(untrack(() => toLeverFormValues(editingLever)))
	let touchedFields = $state<Partial<Record<LeverFormField, boolean>>>({})
	let isSaving = $state(false)

	const isEditMode = $derived(!!editingLever)

	const projectLevers = $derived((project?.resourceConfiguration ?? []).filter(isLeverResource))

	const selectedLever = $derived(projectLevers.find(({ id }) => id === formValues.leverId))

	const resolvedDefaultValue = $derived(
		applyBooleanDefault(selectedLever?.leverType, formValues.defaultValue)
	)

	const valuePlaceholder = $derived(
		selectedLever?.possibleValues?.length ? $t('SetLeverValueModal.selectValue') : undefined
	)

	const leverOptions: DetailedSelectItem[] = $derived(
		projectLevers.map(({ id = '', name = '', leverType }) => {
			const isAlreadySet = !isEditMode && existingLeverIds.includes(id)
			const dataTypeLabel = leverType
				? enumToTranslatedString('Enum.DataType', leverType)
				: undefined

			return {
				id,
				title: name,
				disabled: isAlreadySet,
				description: isAlreadySet ? $t('SetLeverValueModal.alreadyExists') : dataTypeLabel
			}
		})
	)

	const selectedLeverOption = $derived(
		leverOptions.find(({ id }) => id === formValues.leverId) ?? null
	)

	const validationSchema: Record<LeverFormField, FormValidationItem> = $derived({
		leverId: {
			formComponent: formValues.leverId,
			validators: [validateEmptyString]
		},
		defaultValue: {
			validators: getLeverValueValidators(selectedLever?.leverType),
			formComponent: toLeverValidationInput(selectedLever?.leverType, resolvedDefaultValue)
		}
	})

	const errors = $derived(validateForm(validationSchema, touchedFields, false))

	const isFormValid = $derived(
		Object.keys(validateForm(validationSchema, ALL_LEVER_FIELDS_TOUCHED, false)).length === 0
	)

	const handleLeverSelect = (item: DetailedSelectItem | null): void => {
		formValues = { leverId: item?.id ?? '', defaultValue: toLeverFormValue(undefined) }
		touchedFields = { ...touchedFields, leverId: true }
	}

	const handleLeverValueChange = (defaultValue: LeverFormValue): void => {
		formValues = { ...formValues, defaultValue }
		touchedFields = { ...touchedFields, defaultValue: true }
	}

	const handleSubmit = async (event: SubmitEvent): Promise<void> => {
		event.preventDefault()
		touchedFields = { ...ALL_LEVER_FIELDS_TOUCHED }

		if (!selectedLever || !isFormValid || isSaving) return

		const lever: Partial<Lever> = {
			...selectedLever,
			defaultValue: toLeverDefaultValue(selectedLever.leverType, resolvedDefaultValue)
		}

		isSaving = true

		try {
			const isSaved = await onSubmit(lever, isEditMode)

			if (isSaved) {
				onclose()
			}
		} finally {
			isSaving = false
		}
	}
</script>

<form onsubmit={handleSubmit} class="modal-content">
	<div class="form-group">
		<DetailedSelect
			items={leverOptions}
			error={errors.leverId}
			onSelect={handleLeverSelect}
			disabled={!project || isEditMode}
			selectedItem={selectedLeverOption}
			label={$t('SetLeverValueModal.lever')}
			placeholder={$t('SetLeverValueModal.selectLever')}
			displayIds={showNodeIds.value}
		/>
		<LeverValueField
			lever={selectedLever}
			error={errors.defaultValue}
			placeholder={valuePlaceholder}
			formValue={resolvedDefaultValue}
			onChange={handleLeverValueChange}
		/>
	</div>
	<div class="form-footer">
		<Button
			type="submit"
			disabled={isSaving}
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			buttonText={isEditMode ? $t('SetLeverValueModal.save') : $t('SetLeverValueModal.add')}
		/>
		<Button
			type="button"
			onclick={onclose}
			buttonText={$t('Button.Cancel')}
			buttonType={ButtonType.TERTIARY_INVERSE}
			buttonSize={ButtonSize.LARGE}
		/>
	</div>
</form>

<style lang="postcss">
	.modal-content {
		@apply w-[434px] max-w-full flex flex-col gap-8 mt-1;
	}
	.form-group {
		@apply flex flex-col gap-4;
	}
	.form-footer {
		@apply flex gap-4;
	}
</style>
